import querystring from 'querystring';
import type { GetServerSideProps, NextPage } from 'next';
import { z } from 'zod';
import Script from 'next/script';
import { useEffect } from 'react';
import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import { modularPageWrapperSchema } from '../../components/ModularPageWrapper/schema';
import { getCachedEntry } from '../../utils/requests/contentstack/cachedRequests';
import { formatError, serverLogger } from '../../utils/loggers/server';
import { ModularPageWrapper } from '../../components/ModularPageWrapper';
import { isAllSettledResponseFailure, logResponsesFromAllSettledSSR } from '../../utils/promises';
import { getDynamicAdsRequest } from '../../features/dynamicAds/utils/getDynamicAdsRequest';
import { getModulesWithHeroOverride } from '../../features/dynamicAds/utils/getModulesWithHeroOverride';
import { getReactivationUrl, isDynamicAdsLandingPage } from '../../utils/redirects';
import { getContentVariant, getContentstackEntryUid } from '../../utils/getContentVariant/getContentVariant';
import { setOfferOverride } from '../../utils/modules/setOfferOverride';
import { addFeatureFlagsToRumContext } from '../../utils/loggers/browser';
import { type GatewayHeroLayoutAllocationValue, getGatewayHeroLayoutAllocation, overrideHeroModuleByGatewayHeroLayout } from '../../experiments/gatewayHeroLayout';
import { GATEWAY_HERO_LAYOUT_CONFIG_PARAM_NAME } from '../../utils/helpers';
import { jsonLdData } from './jsonLdData';
export type MenPageProps = z.infer<typeof modularPageWrapperSchema> & {
  gatewayHeroLayoutAllocationValue?: GatewayHeroLayoutAllocationValue;
};
const MEN_ENTRY_UIDS = {
  supTarget: 'blt92c5b1250106b191',
  control: 'bltcd969fd413497613',
  q3Redesign: 'blt8088fb7d06f4c110'
};
export const getServerSideProps: GetServerSideProps<MenPageProps> = async ({
  req,
  query
}) => {
  const url = req.url || '';
  const stringifiedQuery = querystring.stringify(query);
  const reactivationUrl = getReactivationUrl(url);
  if (reactivationUrl) {
    return {
      redirect: {
        destination: `${reactivationUrl}?${stringifiedQuery}`,
        permanent: false
      }
    };
  }
  const contentVariant = getContentVariant({
    url
  });
  try {
    const entryRequest = await getCachedEntry({
      contentType: 'modular_page',
      uid: getContentstackEntryUid({
        contentVariant,
        uids: MEN_ENTRY_UIDS
      }),
      schema: modularPageWrapperSchema,
      logger: serverLogger
    });
    let dynamicAdsRequest: ReturnType<typeof getDynamicAdsRequest> | null = null;

    // only call the dynamic ads algos if the page is a dynamic ads landing page
    if (isDynamicAdsLandingPage(url)) {
      dynamicAdsRequest = getDynamicAdsRequest({
        req
      });
    }

    /**
     * this allows us to handle a dynamic number of requests in a non-synchronous way
     */
    const responses = await Promise.allSettled([entryRequest, dynamicAdsRequest]);
    logResponsesFromAllSettledSSR({
      responses,
      message: 'men data error',
      team: 'client-onboarding'
    });
    const [entryResponse, dynamicAdsResponse] = responses;
    const dynamicAdsData = dynamicAdsResponse.status === 'fulfilled' ? dynamicAdsResponse.value?.data : undefined;
    if (isAllSettledResponseFailure(entryResponse)) {
      throw entryResponse.reason;
    }
    const modules = entryResponse.value.data.modules;
    const heroModules = getModulesWithHeroOverride({
      businessLinePage: 'men',
      modules,
      dynamicAds: dynamicAdsData,
      dynamicAdHeroContent: entryResponse.value.data.dynamicAdHeroContent
    });
    const shouldSkipOfferOverride = contentVariant === 'sup-target';
    const modulesWithOfferOverride = shouldSkipOfferOverride ? heroModules : setOfferOverride(heroModules);
    const gatewayHeroLayoutAllocation = await getGatewayHeroLayoutAllocation({
      page: 'men',
      query,
      req,
      url
    });
    const modulesWithHeroLayoutOverride = await overrideHeroModuleByGatewayHeroLayout({
      allocationValue: gatewayHeroLayoutAllocation,
      modules: modulesWithOfferOverride,
      page: 'men'
    });
    return {
      props: {
        seoData: entryResponse.value.data.seoData,
        modules: modulesWithHeroLayoutOverride,
        gatewayHeroLayoutAllocationValue: gatewayHeroLayoutAllocation
      }
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Required page data is undefined - Men',
      context: {
        error: formatError(error),
        page: url,
        severity: 'critical'
      }
    });
    throw new Error('Required data not provided to render Men page');
  }
};
const MenPage: NextPage<MenPageProps> = ({
  seoData,
  modules,
  gatewayHeroLayoutAllocationValue = 'control'
}) => {
  useEffect(() => {
    fireEvent({
      eventName: 'expt_1800',
      eventAttributes: {
        allocationValue: gatewayHeroLayoutAllocationValue
      }
    });
    addFeatureFlagsToRumContext({
      [GATEWAY_HERO_LAYOUT_CONFIG_PARAM_NAME]: gatewayHeroLayoutAllocationValue
    });
  }, [gatewayHeroLayoutAllocationValue]);
  return <>
      <Script
    // Blueshift web conversion integration (https://experiences.getblueshift.com/)
    data-testid="blueshift-script" src="https://www.lightboxcdn.com/vendor/3767d51a-4af9-4270-8ee4-c7310e4371b0/lightbox_speed.js" defer />
      <ModularPageWrapper seoData={seoData} modules={modules} jsonLdData={jsonLdData} />
    </>;
};
export default MenPage;